import { BlobProvider } from '@react-pdf/renderer';
import { format } from 'date-fns/format';
import { FileText } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDatePicker from '../../components/GenericDatePicker';
import GenericDropdown from '../../components/GenericDropDown';
import GenericReportPDF from '../../components/GenericReportPDF';
import GenericTable from '../../components/GenericTable';
import { useAuth } from '../../context/AuthContext';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { BetViewRequest } from '../../models/BetViewRequest';
import { GameConfig } from '../../models/GameConfig';
import { User } from '../../models/Users';
import { getAllActiveGameConfigs } from '../../redux/entities/gameEntity';
import { getBetViewReport } from '../../redux/entities/transactionEntity';
import { getAllUsers } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Role } from '../../utils/rolesUtil';

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const TotalSection = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-weight: 600;
  margin-bottom: 2rem;
`;

const DownloadButton = styled.button`
  background-color: #4a90e2;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  cursor: pointer;
  
  &:hover {
    background-color: #357abd;
  }
`;


const BetViewPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [request, setRequest] = useState<BetViewRequest>({} as BetViewRequest);
  const [selectedGameType, setSelectedGameType] = useState<string>('');
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [resultTimeOptions, setResultTimeOptions] = useState<any[]>([]);
  const [selectedResultTime, setSelectedResultTime] = useState<any>('');

  const [selectedAdmin, setSelectedAdmin] = useState<any>();
  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [selectedCustomer, setSelectedCustomer] = useState<any>();


  const dispatch = useAppDispatch();
  const { showModalStrip } = useModalStrip();
  const rawUsers = useAppSelector((state) => state.entities.userReducer.users);
  const users = useMemo(() => (Array.isArray(rawUsers) ? rawUsers : []), [rawUsers]);
  const gameConfigs = useAppSelector((state) => state.entities.gameReducer.activeGameConfigs);
  const betViews = useAppSelector((state) => state.entities.transactionReducer.betViews);

  const { user, role } = useAuth();

  // Group gameConfigs by game name
  const groupedGameConfigs = useMemo(() => {
    return gameConfigs.reduce((acc, config) => {
      const gameName = config?.game?.name;
      if (!acc[gameName]) {
        acc[gameName] = [];
      }
      acc[gameName].push(config);
      return acc;
    }, {} as Record<string, GameConfig[]>);
  }, [gameConfigs]);

  const SafeDownloadButton: React.FC<{
    onClick: () => void;
    children: React.ReactNode;
  }> = DownloadButton as any;

  const [gameTypeOptions] = useState<any[]>([
    { label: 'Select All', value: 'Select All' },
    { label: 'Single', value: 'Single' },
    { label: 'Patti', value: 'Patti' },
    { label: 'Digit', value: 'Digit' },
  ]);

  // Get the resultTimes based on selected game
  const getResultTimes = useCallback((gameName: string) => {
    const selectedGameConfig = groupedGameConfigs[gameName] || [];
    return selectedGameConfig.map((config: any) => config.resultTime);
  }, [groupedGameConfigs]);

  useEffect(() => {
    dispatch(getAllUsers(0));

  }, [dispatch]);

  useEffect(() => {
    if (user) {
      const userId = user.id?.toString();
      if (role === Role.Customer) {
        setRequest(prev => ({
          ...prev,
          custId: userId
        }));
      }
      else if (role === Role.Agent) {
        setRequest(prev => ({
          ...prev,
          agentId: userId
        }));
      }
      else if (role === Role.Admin) {
        setRequest(prev => ({
          ...prev,
          adminId: userId
        }));
      }
    }

  }, [user, role, users])



  useEffect(() => {
    if (selectedGame === '0') {
      setResultTimeOptions([{ value: '0', label: 'Select All' }]);
    } else if (selectedGame !== '0') {
      const resultTimes = getResultTimes(selectedGame).map(resultTime => ({
        value: resultTime.id.toString(),
        label: resultTime.resultTime,
      }));

      // Sorting the times based on the label (which is in 12-hour format like "10:30 AM")
      const sortedResultTimes = resultTimes.sort((a, b) => {
        const timeA = new Date(`01/01/2020 ${a.label}`); // Adding a fixed date to parse the time correctly
        const timeB = new Date(`01/01/2020 ${b.label}`);
        return timeA.getTime() - timeB.getTime();
      });

      setResultTimeOptions(sortedResultTimes);
    }
  }, [selectedGame, getResultTimes]);

  // Directly filter users based on role
  const getFilteredUsers = (parent: string, parentUser: User | null) => {
    if (parent === Role.Admin) {
      return users.filter((user) => user && user.role === Role.Admin);
    }
    if (parent === Role.Agent) {
      return parentUser ? (parentUser.children ? parentUser.children : []) : users.filter((user) => user && user.role === Role.Agent);
    }
    if (parent === Role.Customer) {
      const customers = parentUser ? (parentUser.children ? parentUser.children : []) : users.filter((user) => user && user.role === Role.Customer);
      return customers;
    }
    return [];
  };

  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    const fetchGameConfigs = async () => {
      if ((!gameConfigs || gameConfigs.length === 0) && !isLoading && !hasFetched) {
        try {
          setIsLoading(true);
          await dispatch(getAllActiveGameConfigs()).unwrap();
          setHasFetched(true);
        } catch (err) {
          showModalStrip('danger', 'Failed to load game configurations', 5000);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchGameConfigs();
  }, [dispatch, gameConfigs, isLoading, hasFetched, showModalStrip]);


  // Handle form submission (save bet)
  const handlePublishResult = () => {
    // Validation
    if (!selectedDate && !selectedGame && !selectedResultTime
      && (!selectedAdmin || !selectedAgent || !selectedCustomer)) {
      alert("Required fields are missing");
    }
    // Dispatch action to save the bet
    dispatch(getBetViewReport(request)).unwrap()
      .catch((err: any) => {
        showModalStrip('danger', err.message, 5000);
      });
  };

  const columns = [
    {
      header: 'Date', field: 'date', render: (rowData: any) => {
        return (
          <span className="single-line-text">
            {format(new Date(rowData.date), 'dd-MM-yyyy')}
          </span>
        );
      }
    },
    { header: 'Customer', field: 'customerName' },
    { header: 'Agent', field: 'agentName' },
    { header: 'Admin', field: 'adminName' },
    { header: 'Game', field: 'game' },
    { header: 'Result Time', field: 'resultTime' },
    {
      header: 'Bet Number',
      field: 'betNumber',
      render: (rowData: any) => formatBetNumberForTable(rowData.betNumber)
    },
    { header: 'Bet Amount', field: 'betPoint' },
    { header: 'Win Amount', field: 'winPoint' },
    {
      header: 'Profit/Loss',
      field: '',
      render: (rowData: any) => {
        const winPoint = rowData?.winPoint; // Replace with actual field for Min value
        const betPoint = rowData?.betPoint; // Replace with actual field for Max value
        const profitLoss = winPoint - betPoint; // Return Min/Max or '-' if data is missing

        const profitLossStyle = profitLoss >= 0 ? { color: 'green' } : { color: 'red' };

        return (
          <span style={profitLossStyle}>
            {profitLoss >= 0 ? `+${profitLoss}` : profitLoss}
          </span>
        );
      }
    },
  ];

  // Calculate totals for txnPoint and winPoint
  const calculateTotals = () => {
    const totalTxnPoint = betViews.reduce((acc, curr) => acc + curr.betPoint, 0);
    const totalWinPoint = betViews.reduce((acc, curr) => acc + curr.winPoint, 0);
    return { totalTxnPoint, totalWinPoint };
  };

  const { totalTxnPoint, totalWinPoint } = calculateTotals();

  // Function to format bet numbers with special handling for * and 9 (for table display)
  const formatBetNumberForTable = (betNumber: string): React.ReactNode => {
    if (!betNumber) return '';

    const placeholders = ['FD', 'LD', 'OP', 'EP', 'R', 'BURFI'];

    // Handle patterns like 1*99999*99999...
    const parts = betNumber.split('*');
    if (parts.length > 1) {
      return (
        <div style={{ display: 'flex', gap: '4px' }}>
          {parts.map((part, idx) => {
            if (part === '99999' || part === '') {
              return (
                <span key={idx} style={{
                  border: '1px solid #ccc',
                  padding: '0 4px',
                  minWidth: '20px',
                  textAlign: 'center'
                }}>
                  {placeholders[idx] || ''}
                </span>
              );
            }
            return (
              <span key={idx} style={{
                border: '1px solid #ccc',
                padding: '0 4px',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {part}
              </span>
            );
          })}
        </div>
      );
    }

    // If it's a star pattern (5 stars)
    // if (betNumber === '*****') {
    //   return (
    //     <div style={{ display: 'flex', gap: '4px' }}>
    //       {placeholders.map((ph, idx) => (
    //         <span key={idx} style={{ 
    //           border: '1px solid #ccc',
    //           padding: '0 4px',
    //           minWidth: '20px',
    //           textAlign: 'center'
    //         }}>
    //           {ph}
    //         </span>
    //       ))}
    //     </div>
    //   );
    // }

    // If it's all 9s (99999)
    // if (betNumber === '99999') {
    //   return (
    //     <div style={{ display: 'flex', gap: '4px' }}>
    //       {placeholders.map((ph, idx) => (
    //         <span key={idx} style={{ 
    //           border: '1px solid #ccc',
    //           padding: '0 4px',
    //           minWidth: '20px',
    //           textAlign: 'center'
    //         }}>
    //           {ph}
    //         </span>
    //       ))}
    //     </div>
    //   );
    // }

    // For regular numbers
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        <span style={{
          border: '1px solid #ccc',
          padding: '0 4px',
          minWidth: '20px',
          textAlign: 'center'
        }}>
          {betNumber}
        </span>
      </div>
    );


  };

  // Function to format bet numbers as text (for PDF)
  const formatBetNumberAsText = (betNumber: string, gameType?: string): string => {
    if (!betNumber) return '';

    if (gameType !== 'Barfi') {
      return betNumber;
    }

    const placeholders = ['FD', 'LD', 'OP', 'EP', 'R', 'BARFI'];

    // Handle patterns like 1*99999*99999...
    const parts = betNumber.split('*');
    if (parts.length > 1) {
      return parts
        .map((part, idx) => part === '99999' || part === '' ? placeholders[idx] || '' : part)
        .filter(Boolean)
        .join(' ');
    }

    if (betNumber === '*****' || betNumber === '99999') {
      return placeholders.join(' ');
    }

    // Handle numbers separated by * (e.g., 1*4*13*24*0*1234)
    if (betNumber.includes('*') && !betNumber.includes('9')) {
      return betNumber.split('*').filter(Boolean).join(' ');
    }

    if (betNumber.includes('*') || betNumber.includes('9')) {
      let result = '';

      for (let i = 0; i < betNumber.length; i++) {
        const char = betNumber[i];
        if (char === '*' || char === '9') {
          result += placeholders[i] + ' ';
        } else {
          result += char + ' ';
        }
      }

      return result.trim();
    }

    return betNumber;
  };

  return (
    <Container>
      <PageHeading>Bet Report</PageHeading>
      <TableWrapper>

        <GenericDatePicker
          label='Select Date'
          value={selectedDate ?? null}

          onChange={(val) => {
            setSelectedDate(val ?? undefined);
            setRequest({ ...request, date: format(val == null ? new Date() : val, "yyyy-MM-dd") });
          }}

        />

        <GenericDropdown
          label="Select Game"
          options={[
            { value: '0', label: 'Select All' },
            ...Object.keys(groupedGameConfigs).map((gameName) => ({
              value: gameName,
              label: gameName,
            }))
          ]}
          onChange={(gameName) => {
            setSelectedGame(gameName);
            setRequest({ ...request, gameId: groupedGameConfigs[gameName] && gameName?.id !== '0' ? groupedGameConfigs[gameName][0]?.game?.id : undefined });
          }}
          value={selectedGame}
        />

        {/* Select Result Time */}
        <GenericDropdown
          label="Select Result Time"
          options={resultTimeOptions}
          onChange={(resultTime) => {
            setSelectedResultTime(resultTime);
            setRequest({ ...request, resultTimeId: resultTime !== '' && resultTime !== '0' ? resultTime : undefined });
          }}
          value={selectedResultTime}
        />


        <GenericDropdown
          label="Select Game Type"
          options={gameTypeOptions}
          onChange={(gameType) => {
            setSelectedGameType(gameType);
            setRequest({ ...request, gameType: gameType !== null && gameType !== 'Select All' ? gameType : undefined });
          }}
          value={selectedGameType}
        />

        {/* SuperAdmin selects Admin */}
        {role === Role.SuperAdmin && (
          <GenericDropdown
            label="Select Admin"
            options={[
              { value: '0', label: 'Select All' },
              ...getFilteredUsers(Role.Admin, null)?.map((admin) => ({
                value: admin.id.toString(),
                label: admin.name,
              }))
            ]}
            onChange={(value) => {
              if (value === '0') {
                setSelectedAdmin({ id: '0', label: 'Select All' });
                setRequest({ ...request, adminId: undefined });
              } else {
                setSelectedAdmin(users?.find((user) => user?.id?.toString() === value));
                setRequest({ ...request, adminId: users?.find((user) => user?.id?.toString() === value)?.id?.toString() });
              }
            }}
            value={selectedAdmin?.id?.toString()}
          />
        )}

        {/* Admin selects Agent */}
        {(role === Role.SuperAdmin || role === Role.Admin) && (
          <GenericDropdown
            label="Select Agent"
            options={[
              { value: '0', label: 'Select All' },
              ...getFilteredUsers(Role.Agent, selectedAdmin)?.map((agent) => ({
                value: agent.id.toString(),
                label: agent.name,
              }))
            ]}
            onChange={(value) => {
              if (value === '0') {
                setSelectedAgent({ id: '0', label: 'Select All' });
                setRequest({ ...request, agentId: undefined });
              } else {
                setSelectedAgent(selectedAdmin
                  ? selectedAdmin.children?.find((user: any) => user?.id?.toString() === value)
                  : users.find((user) => user?.id?.toString() === value))
                setRequest({
                  ...request, agentId: selectedAdmin
                    ? selectedAdmin.children?.find((user: any) => user?.id?.toString() === value)?.id
                    : users.find((user) => user?.id?.toString() === value)?.id
                });
              }
            }}
            value={selectedAgent?.id?.toString()}
          />
        )}

        {/* Agent selects Customer */}
        {(role === Role.SuperAdmin || role === Role.Admin || role === Role.Agent) && (
          <GenericDropdown
            label="Select Customer"
            options={[
              { value: '0', label: 'Select All' },
              ...getFilteredUsers(Role.Customer, selectedAgent)?.map((agent) => ({
                value: agent.id.toString(),
                label: agent.name,
              }))
            ]}
            onChange={(value) => {
              if (value === '0') {
                setSelectedCustomer({ id: '0', label: 'Select All' });
                setRequest({ ...request, custId: undefined });
              } else {
                setSelectedCustomer(selectedAgent
                  ? selectedAgent.children?.find((user: any) => user?.id?.toString() === value)
                  : users.find((user) => user?.id?.toString() === value))
                setRequest({
                  ...request, custId: selectedAgent
                    ? selectedAgent.children?.find((user: any) => user?.id?.toString() === value)?.id
                    : users.find((user) => user?.id?.toString() === value)?.id
                });
              }

            }}
            value={selectedCustomer?.id?.toString()}
          />
        )}

        <GenericButton
          label="Fetch"
          onClick={() => handlePublishResult()}
        />

        {betViews && betViews.length > 0 && (
          <BlobProvider
            document={
              <GenericReportPDF
                title="Bet Report"
                reportType="Bet View Report"
                gameDetails={{
                  gameName: selectedGame || 'All Games',
                  gameType: selectedGameType || 'All Types',
                  resultTime: selectedResultTime || 'All Times'
                }}
                dateRange={{
                  startDate: selectedDate || undefined,
                }}
                columns={[
                  { header: 'Date', field: 'date', render: (row) => format(new Date(row.date), 'dd-MM-yyyy') },
                  { header: 'Customer', field: 'customerName' },
                  { header: 'Agent', field: 'agentName' },
                  { header: 'Admin', field: 'adminName' },
                  { header: 'Game', field: 'game' },
                  { header: 'Result Time', field: 'resultTime' },
                  {
                    header: 'Bet Number',
                    field: 'betNumber',
                    render: (row: any) => formatBetNumberAsText(row.betNumber, row.gameType)
                  },
                  { header: 'Bet Amount', field: 'betPoint' },
                  { header: 'Win Amount', field: 'winPoint' },
                  {
                    header: 'Profit/Loss',
                    field: '',
                    render: (row) => {
                      const profitLoss = row.winPoint - row.betPoint;
                      return profitLoss >= 0 ? `+${profitLoss}` : `${profitLoss}`;
                    }
                  }
                ]}
                data={[...betViews].sort((a: any, b: any) => {
                  // First sort by game
                  const gameCompare = (a.game || '').localeCompare(b.game || '');
                  if (gameCompare !== 0) return gameCompare;

                  // Then by resultTime
                  const timeCompare = (a.resultTime || '').localeCompare(b.resultTime || '');
                  if (timeCompare !== 0) return timeCompare;

                  // Finally by betPoint (descending order)
                  return (b.betPoint || 0) - (a.betPoint || 0);
                })}
                summary={{
                  totalBetAmount: totalTxnPoint,
                  totalWinAmount: totalWinPoint,
                  profitLoss: totalWinPoint - totalTxnPoint
                }}
              />
            }
          >
            {({ url }) => (
              <SafeDownloadButton
                onClick={() => {
                  if (url) {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `bet-report-${format(selectedDate || new Date(), 'dd-MM-yyyy')}.pdf`;
                    link.click();
                  }
                }}
              >
                <FileText /> Download Report
              </SafeDownloadButton>
            )}
          </BlobProvider>
        )}


        <GenericTable
          columns={columns}
          data={[...betViews].sort((a: any, b: any) => {
            // First sort by game
            const gameCompare = (a.game || '').localeCompare(b.game || '');
            if (gameCompare !== 0) return gameCompare;

            // Then by resultTime
            const timeCompare = (a.resultTime || '').localeCompare(b.resultTime || '');
            if (timeCompare !== 0) return timeCompare;

            // Finally by betPoint (descending order)
            return (b.betPoint || 0) - (a.betPoint || 0);
          })}
          actions={false}
          pagination={false}
        />


      </TableWrapper>

      {/* Total Section */}
      <TotalSection>
        <div><strong>Total Bet Amount:</strong> {totalTxnPoint}</div>
        <div><strong>Total Win Amount:</strong> {totalWinPoint}</div>
        <div>
          <strong>Profit/Loss: </strong>
          {(() => {
            const profitLoss = totalTxnPoint - totalWinPoint;
            const profitLossStyle = profitLoss >= 0 ? { color: 'green' } : { color: 'red' };
            return (
              <span style={profitLossStyle}>
                {profitLoss >= 0 ? `+${profitLoss}` : profitLoss}
              </span>
            );
          })()}
        </div>
      </TotalSection>

    </Container>
  );
};

export default BetViewPage;
